import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Building2, Phone, Mail, FileText, Briefcase, ArrowLeft, ExternalLink,
  Edit2, IndianRupee, TrendingUp, Globe, MapPin, Hash, Plus,
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../lib/formatUtils';
import { ProjectLink } from '../components/links/EntityLinks';
import EditClientDialog from '../components/clients/EditClientDialog';
import ProjectFormDialog from '../components/projects/ProjectFormDialog';
import TruncatedText from '../components/common/TruncatedText';
import { toast } from 'sonner';

// Inline INR currency formatter — `formatUtils` only exports date helpers.
const formatCurrency = (amt) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })
    .format(Number(amt) || 0);

/**
 * Standalone Client detail page. Mirrors ProjectDetailPage's layout so the
 * navigation feels consistent. Loads the client + its projects/invoices/
 * transactions lazily (tab-driven) so the initial paint stays fast.
 */
export default function ClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [projects, setProjects] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [transactions, setTransactions] = useState(null);
  const [stats, setStats] = useState({ projects: 0, invoices: 0, billed: 0, received: 0 });
  const [editOpen, setEditOpen] = useState(false);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);

  // Re-fetches the client's projects + project count after the Add-Project
  // dialog closes successfully. Keeps the stat strip + projects tab in sync
  // without needing a full page reload.
  const refreshProjects = async () => {
    try {
      const res = await api.get(`/projects?client_id=${id}&limit=200&sort_by=created_at&sort_order=desc`);
      const list = res.data?.data || [];
      setProjects(list);
      setStats(s => ({ ...s, projects: res.data?.total ?? list.length }));
    } catch {
      /* no-op — toast is shown by the dialog on failure */
    }
  };

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    api.get(`/clients/${id}`)
      .then(r => setClient(r.data?.data || r.data))
      .catch(() => toast.error('Failed to load client'))
      .finally(() => setLoading(false));
    // Eagerly load stats (cheap counts) so the strip renders immediately.
    Promise.all([
      api.get(`/projects?client_id=${id}&limit=200`),
      api.get(`/invoices?client_id=${id}&limit=500`),
    ]).then(([p, i]) => {
      const invs = i.data?.data || [];
      setStats({
        projects: p.data?.total ?? (p.data?.data || []).length,
        invoices: i.data?.total ?? invs.length,
        billed: invs.reduce((s, x) => s + (Number(x.total) || 0), 0),
        received: invs
          .filter(x => x.status === 'paid' || x.payment_received)
          .reduce((s, x) => s + (Number(x.total) || 0), 0),
      });
    }).catch(() => {});
  }, [id]);

  // Lazy-load each tab's data on first visit.
  useEffect(() => {
    if (!id) return;
    if (tab === 'projects' && projects === null) {
      api.get(`/projects?client_id=${id}&limit=200&sort_by=created_at&sort_order=desc`)
        .then(r => setProjects(r.data?.data || []))
        .catch(() => setProjects([]));
    }
    if (tab === 'invoices' && invoices === null) {
      api.get(`/invoices?client_id=${id}&limit=200&sort_by=invoice_date&sort_order=desc`)
        .then(r => setInvoices(r.data?.data || []))
        .catch(() => setInvoices([]));
    }
    if (tab === 'transactions' && transactions === null) {
      api.get(`/transactions?client_id=${id}&limit=200`)
        .then(r => setTransactions(r.data?.data || []))
        .catch(() => setTransactions([]));
    }
  }, [tab, id, projects, invoices, transactions]);

  if (loading) {
    return <div className="p-8 text-muted-foreground" data-testid="client-loading">Loading client…</div>;
  }
  if (!client) {
    return (
      <div className="p-8" data-testid="client-not-found">
        <p className="text-muted-foreground">Client not found.</p>
        <Button variant="outline" onClick={() => navigate('/clients')} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" />Back to Clients
        </Button>
      </div>
    );
  }

  const outstanding = stats.billed - stats.received;
  const billingEmails = (() => {
    const raw = client.billing_emails;
    if (Array.isArray(raw)) {
      return raw
        .map(x => typeof x === 'string' ? x : (x?.email || x?.address || ''))
        .filter(Boolean);
    }
    if (typeof raw === 'string') return raw.split(/[,;]/).map(s => s.trim()).filter(Boolean);
    if (raw && typeof raw === 'object') return [raw.email || raw.address].filter(Boolean);
    return [];
  })();

  return (
    <div className="p-6 space-y-6" data-testid="client-detail-page">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/clients')} data-testid="client-back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" />Back
        </Button>
        <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
          <Building2 className="h-6 w-6 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-semibold truncate" data-testid="client-name">
              {client.company_name || client.name || 'Unnamed Client'}
            </h1>
            <Badge variant="outline" className={
              (client.status === 'active') ? 'border-emerald-500/40 text-emerald-500' :
              'border-gray-500/40 text-muted-foreground'
            }>
              {client.status || 'active'}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            ID {client.id?.slice(0, 8)} · Added {formatDate(client.created_at)}
          </p>
        </div>
        {hasPermission('clients.edit') && (
          <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} data-testid="client-edit-btn">
            <Edit2 className="h-4 w-4 mr-1.5" />Edit
          </Button>
        )}
        {hasPermission('projects.create') && (
          <Button
            size="sm"
            onClick={() => setProjectDialogOpen(true)}
            data-testid="client-add-project-btn"
          >
            <Plus className="h-4 w-4 mr-1.5" />Add Project
          </Button>
        )}
      </div>

      {/* Add/Edit project — locked to this client so it can't be reassigned */}
      <ProjectFormDialog
        open={projectDialogOpen}
        onOpenChange={setProjectDialogOpen}
        defaultClientId={id}
        lockClient
        onSaved={() => {
          refreshProjects();
          setTab('projects');
        }}
      />

      {/* Edit dialog — opens inline so the user stays on the detail page */}
      <EditClientDialog
        clientId={id}
        open={editOpen}
        onOpenChange={setEditOpen}
        onSaved={(updated) => { if (updated) setClient(prev => ({ ...prev, ...updated })); }}
      />

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card data-testid="client-stat-projects">
          <CardContent className="p-4 flex items-center gap-3">
            <Briefcase className="h-5 w-5 text-blue-500" />
            <div>
              <div className="text-2xl font-semibold">{stats.projects}</div>
              <div className="text-xs text-muted-foreground">Projects</div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="client-stat-invoices">
          <CardContent className="p-4 flex items-center gap-3">
            <FileText className="h-5 w-5 text-amber-500" />
            <div>
              <div className="text-2xl font-semibold">{stats.invoices}</div>
              <div className="text-xs text-muted-foreground">Invoices</div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="client-stat-billed">
          <CardContent className="p-4 flex items-center gap-3">
            <IndianRupee className="h-5 w-5 text-emerald-500" />
            <div>
              <div className="text-xl font-semibold">{formatCurrency(stats.billed)}</div>
              <div className="text-xs text-muted-foreground">Total Billed</div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="client-stat-outstanding">
          <CardContent className="p-4 flex items-center gap-3">
            <TrendingUp className={`h-5 w-5 ${outstanding > 0 ? 'text-red-500' : 'text-emerald-500'}`} />
            <div>
              <div className="text-xl font-semibold">{formatCurrency(outstanding)}</div>
              <div className="text-xs text-muted-foreground">Outstanding</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="projects" data-testid="tab-projects">Projects ({stats.projects})</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices ({stats.invoices})</TabsTrigger>
          <TabsTrigger value="transactions" data-testid="tab-transactions">Transactions</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <Card>
            <CardContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <Field icon={<User2Phone />} label="Contact Person" value={client.contact_person} />
              <Field icon={<Phone className="h-4 w-4" />} label="Phone" value={client.phone} />
              <Field icon={<Mail className="h-4 w-4" />} label="Primary Email" value={client.email} />
              <Field icon={<Mail className="h-4 w-4" />} label="Billing Emails"
                value={billingEmails.length === 0
                  ? <span className="text-red-400 italic">Not set — required for tax-invoice emails</span>
                  : <div className="flex flex-wrap gap-1.5">
                      {billingEmails.map((e, i) => <Badge key={i} variant="outline" className="font-normal">{e}</Badge>)}
                    </div>} />
              <Field icon={<Hash className="h-4 w-4" />} label="GSTIN" value={client.gstin} />
              <Field icon={<Hash className="h-4 w-4" />} label="PAN" value={client.pan} />
              <Field icon={<MapPin className="h-4 w-4" />} label="Address" value={client.address || client.company_address} />
              <Field icon={<MapPin className="h-4 w-4" />} label="State" value={client.state} />
              <Field icon={<Globe className="h-4 w-4" />} label="Website"
                value={client.website
                  ? <a href={client.website} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
                      {client.website}<ExternalLink className="h-3 w-3" />
                    </a>
                  : null} />
              <Field icon={<Briefcase className="h-4 w-4" />} label="Industry" value={client.industry} />
              {client.notes && (
                <div className="md:col-span-2 pt-2 border-t border-border/50">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Notes</div>
                  <div className="whitespace-pre-wrap text-foreground/90">{client.notes}</div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="projects" className="mt-4">
          <ItemTable
            data={projects}
            empty="No projects yet for this client."
            head={['Project', 'Status', 'Started', 'Members']}
            row={p => [
              <ProjectLink key="link" id={p.id} name={p.name} />,
              <Badge key="status" variant="outline" className="capitalize">{p.status || '—'}</Badge>,
              formatDate(p.start_date || p.created_at),
              p.member_count ?? (p.members?.length || 0),
            ]}
            testId="client-projects-table"
          />
        </TabsContent>

        <TabsContent value="invoices" className="mt-4">
          <ItemTable
            data={invoices}
            empty="No invoices yet for this client."
            head={['Invoice #', 'Type', 'Date', 'Total', 'Status']}
            row={i => [
              <Link key="link" to={`/invoices?search=${i.invoice_number}`}
                className="text-primary hover:underline decoration-dotted underline-offset-2"
                data-testid={`client-invoice-link-${i.id}`}>
                {i.invoice_number}
              </Link>,
              <Badge key="type" variant="outline" className="capitalize">{i.invoice_type || 'tax'}</Badge>,
              formatDate(i.invoice_date),
              formatCurrency(i.total || 0),
              <Badge key="status" variant="outline" className={
                i.status === 'paid' ? 'border-emerald-500/40 text-emerald-500' :
                i.status === 'unpaid' ? 'border-amber-500/40 text-amber-500' :
                'border-gray-500/40 text-muted-foreground'
              }>{i.status || 'unpaid'}</Badge>,
            ]}
            testId="client-invoices-table"
          />
        </TabsContent>

        <TabsContent value="transactions" className="mt-4">
          <ItemTable
            data={transactions}
            empty="No transactions yet for this client."
            head={['Date', 'Type', 'Description', 'Amount', 'Invoice']}
            row={t => [
              formatDate(t.date),
              <Badge key="type" variant="outline" className={
                t.type === 'credit' ? 'border-emerald-500/40 text-emerald-500' :
                'border-red-500/40 text-red-500'
              }>{t.type}</Badge>,
              <TruncatedText key="desc" text={t.description} lines={2} className="text-sm max-w-[300px]" />,
              formatCurrency(t.amount || 0),
              t.invoice_number || '—',
            ]}
            testId="client-transactions-table"
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const Field = ({ icon, label, value }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5 flex items-center gap-1.5">
      <span className="opacity-70">{icon}</span>{label}
    </div>
    <div className="text-sm text-foreground/90">{value || <span className="text-muted-foreground">—</span>}</div>
  </div>
);

// Tiny inline icon for Contact Person (no perfect lucide match)
const User2Phone = () => <Phone className="h-4 w-4" />;

const ItemTable = ({ data, empty, head, row, testId }) => {
  if (data === null) return <div className="text-sm text-muted-foreground p-4">Loading…</div>;
  if (!data.length) return <div className="text-sm text-muted-foreground p-6 text-center border border-dashed border-border rounded-md">{empty}</div>;
  return (
    <Card>
      <CardContent className="p-0">
        <Table data-testid={testId}>
          <TableHeader>
            <TableRow>{head.map((h, i) => <TableHead key={i}>{h}</TableHead>)}</TableRow>
          </TableHeader>
          <TableBody>
            {data.map((d, i) => (
              <TableRow key={d.id || i}>
                {row(d).map((c, j) => <TableCell key={j}>{c}</TableCell>)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};
